import { getAnalyticsEvents } from './analyticsService';
import { getCollection } from './firestoreService';
import { getAllSubscriptions } from './subscriptionService';

export interface GeographicDistribution {
  country: string;
  users: number;
  revenue: number;
  subscriptions: number;
}

export interface GeographicAnalytics {
  distribution: GeographicDistribution[];
  platformByRegion: Map<string, { ios: number; android: number; web: number }>;
  languagePreferences: Map<string, number>;
  timeZoneUsage: Map<string, number>;
}

export const getGeographicAnalytics = async (): Promise<GeographicAnalytics> => {
  try {
    const [events, users, subscriptions] = await Promise.all([
      getAnalyticsEvents(90),
      getCollection('users', undefined, undefined, undefined, 10000),
      getAllSubscriptions(),
    ]);

    const userCountryMap = new Map<string, string>();
    const countryUsers = new Map<string, Set<string>>();
    const platformByRegion = new Map<string, { ios: number; android: number; web: number }>();
    const timeZoneUsage = new Map<string, number>();

    for (const event of events) {
      const locationInfo = event.properties?.location_info as Record<string, any> | undefined;
      const deviceInfo = event.properties?.device_info as Record<string, any> | undefined;
      if (!locationInfo) continue;

      const country = locationInfo.country || locationInfo.countryName || '';
      const timezone = locationInfo.timezone || '';
      const userId = event.userId;

      if (!country) continue;

      if (!userCountryMap.has(userId)) {
        userCountryMap.set(userId, country);
      }

      if (!countryUsers.has(country)) {
        countryUsers.set(country, new Set());
      }
      countryUsers.get(country)!.add(userId);

      if (timezone) {
        timeZoneUsage.set(timezone, (timeZoneUsage.get(timezone) || 0) + 1);
      }

      const platform = deviceInfo?.platform || event.properties?.platform || '';
      if (country && platform) {
        if (!platformByRegion.has(country)) {
          platformByRegion.set(country, { ios: 0, android: 0, web: 0 });
        }
        const regionData = platformByRegion.get(country)!;
        const p = String(platform).toLowerCase();
        if (p.includes('ios') || p.includes('macos')) regionData.ios++;
        else if (p.includes('android')) regionData.android++;
        else if (p.includes('web')) regionData.web++;
      }
    }

    const subsByUser = new Map<string, number>();
    const subRevenueByUser = new Map<string, number>();
    for (const sub of subscriptions) {
      if (sub.status === 'active' || sub.status === 'trialing') {
        subsByUser.set(sub.userId, (subsByUser.get(sub.userId) || 0) + 1);
        subRevenueByUser.set(sub.userId, (subRevenueByUser.get(sub.userId) || 0) + (sub.amount || 0));
      }
    }

    const distribution: GeographicDistribution[] = [];
    for (const [country, userSet] of countryUsers.entries()) {
      let revenue = 0;
      let subs = 0;
      for (const uid of userSet) {
        revenue += subRevenueByUser.get(uid) || 0;
        subs += subsByUser.get(uid) || 0;
      }
      distribution.push({
        country,
        users: userSet.size,
        revenue: Math.round(revenue * 100) / 100,
        subscriptions: subs,
      });
    }
    distribution.sort((a, b) => b.users - a.users);

    const languagePreferences = new Map<string, number>();
    users.forEach((user: any) => {
      const language = user.languagePreference || user.language || 'en';
      languagePreferences.set(language, (languagePreferences.get(language) || 0) + 1);
    });

    return {
      distribution,
      platformByRegion,
      languagePreferences,
      timeZoneUsage,
    };
  } catch (error) {
    console.error('Error calculating geographic analytics:', error);
    throw error;
  }
};
